import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Bell,
  Check,
  CheckCheck,
  Calendar,
  CalendarCheck,
  FileText,
  Users,
  AlertCircle,
  Loader2,
  Gift,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success("Toutes les notifications ont été marquées comme lues");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      leave_request: Calendar,
      leave_approved: Check,
      leave_rejected: AlertCircle,
      meeting: Users,
      payslip: FileText,
      entretien_annuel: CalendarCheck,
      entretien_trimestriel: CalendarCheck,
      rappel_entretien: Bell,
      anniversaire: Gift,
      system: Bell,
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type) => {
    const colors = {
      leave_request:         "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
      leave_approved:        "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
      leave_rejected:        "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
      meeting:               "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
      payslip:               "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
      entretien_annuel:      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
      entretien_trimestriel: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
      rappel_entretien:      "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
      anniversaire:          "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
      ndf:                   "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
      onboarding:            "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
      system:                "bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400",
    };
    return colors[type] || "bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400";
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return dateString;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] dark:text-[#4B8BF4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Notifications
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1">
            {unreadCount > 0
              ? `Vous avez ${unreadCount} notification${unreadCount > 1 ? "s" : ""} non lue${unreadCount > 1 ? "s" : ""}`
              : "Toutes vos notifications sont lues"}
          </p>
        </div>

        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={handleMarkAllAsRead}
            className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F5E9] dark:border-[#1A4D2E] dark:text-[#4CAF70] dark:hover:bg-[#1A4D2E]/20"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Tout marquer comme lu
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card className="card-neumorphic">
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF] dark:text-[#3D607E]">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-[#1A3050]">
              {notifications.map((notification, index) => {
                const Icon = getNotificationIcon(notification.notification_type);
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-4 p-4 md:p-6 transition-all hover:bg-gray-50 dark:hover:bg-[#112240] animate-fade-in stagger-${(index % 5) + 1}
                      ${!notification.is_read ? "bg-[#E8F5E9]/30 dark:bg-[#1A4D2E]/10" : ""}`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`p-3 rounded-xl shrink-0 ${getNotificationColor(notification.notification_type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${!notification.is_read ? "text-[#111827] dark:text-[#D6E4F5]" : "text-[#4B5563] dark:text-[#7EA8CE]"}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-[#4B5563] dark:text-[#7EA8CE] mt-1">
                            {notification.message}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <Badge className="bg-[#1A4D2E] text-white shrink-0">Nouveau</Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-[#9CA3AF] dark:text-[#3D607E]">
                          {formatDate(notification.created_at)}
                        </span>
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#1A4D2E] hover:bg-[#E8F5E9] dark:text-[#4CAF70] dark:hover:bg-[#1A4D2E]/20"
                            onClick={() => handleMarkAsRead(notification.id)}
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Marquer comme lu
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
